import { useState, useEffect } from "react";
import { useBookmarks } from "@/hooks/use-bookmarks";
import type { SavedKind } from "@/lib/saved-items.functions";

type Props = {
  id: string;
  /** Hvilken slags element gemmes – bruges til at holde artikler/opskrifter/videoer adskilt i "Min samling". */
  kind?: SavedKind;
  className?: string;
  compact?: boolean;
};

/**
 * "+ Gem" / "✓ Gemt" – to adskilte tap-mål på et kort. Bruger localStorage
 * via useBookmarks og synker automatisk til skyen, når man er logget ind.
 * Klik stopper propagering, så det ikke også åbner outbound-linket.
 */
export function SaveButton({ id, kind = "article", className = "", compact = false }: Props) {
  const { toggle, isBookmarked } = useBookmarks();
  const [saved, setSaved] = useState(false);
  const [whisper, setWhisper] = useState(false);

  useEffect(() => {
    setSaved(isBookmarked(kind, id));
  }, [id, kind, isBookmarked]);

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(kind, id);
    const nowSaved = !saved;
    setSaved(nowSaved);
    if (nowSaved) {
      setWhisper(true);
      window.setTimeout(() => setWhisper(false), 1600);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={saved}
      aria-label={saved ? "Fjern fra din samling" : "Gem i din samling"}
      className={`inline-flex items-center gap-1.5 font-serif ${
        compact ? "text-[12px]" : "text-[13px]"
      } tracking-[0.02em] transition-colors ${
        saved ? "text-accent" : "text-ink-soft hover:text-accent"
      } focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 min-h-[36px] min-w-[36px] justify-end ${className}`}
    >
      <span aria-hidden="true">{saved ? "✓" : "+"}</span>
      <span>{saved ? "Gemt" : "Gem"}</span>
      {whisper && (
        <span role="status" className="sr-only">
          Lagt i din samling
        </span>
      )}
    </button>
  );
}
